import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Breadcrumbs from "#/components/breadcrumbs";
import Footer from "#/components/footer/footer";
import CreateJobAlert from "#/components/jobs/CreateJobAlert";
import HiringOrgHeader from "#/components/jobs/HiringOrgHeader";
import MDXRemote from "#/components/MdxRemote";
import NavBar from "#/components/navbar/navbar";
import ApplyBtn from "#/components/job_posting/ApplyBtn";
import EmployerAbout from "#/components/job_posting/EmployerAbout";
import JobLocation from "#/components/job_posting/JobLocation";
import SimilarJobsList from "#/components/job_posting/SimilarJobsList";
import { taxonomyById } from "#/lib/taxonomies/taxonomy_tools";
import type { HiringOrg, Image } from "#/types/types";
import type { JobPostingPageData } from "#/features/job-posting/job-posting.functions";

export default function JobPostingPage({ data }: { data: JobPostingPageData }) {
  const taxonomy = taxonomyNameFromPosting(data.posting.primaryRoleTaxonomyId);
  const breadcrumbs = {
    firstCrumb: {
      href: "/healthcare-job-search",
      textOne: "Healthcare",
      textTwo: "job search",
    },
    secondCrumb: {
      textOne: taxonomy,
      textTwo: "job posting",
    },
  };
  const links = data.posting.slug?.includes("physician")
    ? [
        { href: "/physician-salary-in-united-states", text: "Physician Salary Guide" },
        { href: "/physician-cv-examples", text: "Resume CV Examples" },
      ]
    : [
        { href: "/nurse-salary-in-united-states", text: "Nurse Salary Guide" },
        { href: "/nurse-resume-examples", text: "Resume Examples" },
      ];

  const coverImage = data.hiringOrg ? getImage(data.hiringOrg, "COVER") : null;
  const logoImage = data.hiringOrg ? getImage(data.hiringOrg, "LOGO") : null;

  return (
    <>
      <header>
        <NavBar overrideLinks={links} />
        <Breadcrumbs first={breadcrumbs.firstCrumb} second={breadcrumbs.secondCrumb} />
      </header>
      <main
        id="job-posting"
        className="mx-auto mt-5 max-w-2xl space-y-5 text-gray-900"
      >
        <article>
          <header>
            <HiringOrgHeader
              title={data.posting.title ?? ""}
              coverImage={coverImage}
              logoImage={logoImage}
            />
          </header>

          <section className="mx-5 pt-10">
            <DatePosted datePosted={data.posting.schema?.datePosted} />

            <h2 className="mb-5 text-xl text-sky-600">Job Posting Description</h2>

            <p className="mb-3">{data.posting.mirror?.title}</p>

            <div className="job-posting-description">
              <MDXRemote source={data.posting.description ?? ""} />
            </div>

            <div className="mt-10 flex">
              <button
                type="button"
                className="flex w-full items-center justify-center space-x-2 rounded-lg bg-green-600 px-4 py-4 text-xl text-white shadow-lg hover:bg-green-700"
                role="dialog"
                aria-modal="true"
                aria-labelledby="Compare job posting"
              >
                <PlusCircleIcon className="h-6 w-6" />
                <span>Compare to other Job Offers</span>
              </button>
            </div>

            <TypeOfEmployment employmentType={data.posting.schema?.employmentType} />

            <JobLocation posting={data.posting} />
          </section>

          <EmployerAbout hiringOrg={data.hiringOrg} />

          <div className="mx-8 mt-10 flex flex-col">
            <ApplyBtn jobPostId={data.posting.id} />

            <div className="mt-3 flex space-x-2">
              <button
                type="button"
                className="grow rounded-lg bg-sky-400 px-4 py-2 text-center text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-4 focus:ring-blue-500"
                role="dialog"
                aria-modal="true"
                aria-labelledby="Save job posting"
              >
                Save
              </button>
              <button
                type="button"
                className="grow rounded-lg bg-sky-400 px-4 py-2 text-center text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-4 focus:ring-blue-500"
                role="dialog"
                aria-modal="true"
                aria-labelledby="Share job posting"
              >
                Share
              </button>
            </div>
          </div>
        </article>

        <div className="mx-5 pt-16">
          <CreateJobAlert />
        </div>

        {data.similarJobs.length ? (
          <aside className="space-y-3 pt-10">
            <h2 className="mx-5 mb-5 mt-10 text-xl text-sky-600">
              Similar Nearby Job Postings
            </h2>
            <SimilarJobsList postings={data.similarJobs} />
          </aside>
        ) : null}
      </main>
      <Footer />
      <JsonLdScript jsonLd={data.jsonLd} />
    </>
  );
}

function JsonLdScript({ jsonLd }: { jsonLd: string | null }) {
  if (!jsonLd) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLd }}
    />
  );
}

function DatePosted({ datePosted }: { datePosted: string | undefined }) {
  if (!datePosted) {
    return null;
  }

  const date = new Date(datePosted).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return <p className="text-sm text-gray-500">Posted {date}</p>;
}

function TypeOfEmployment({
  employmentType,
}: {
  employmentType: string | string[] | undefined;
}) {
  const effectiveEmploymentType = Array.isArray(employmentType)
    ? employmentType[0]
    : employmentType;

  if (!effectiveEmploymentType) {
    return null;
  }

  const labels: Record<string, string> = {
    CONTRACTOR: "Contractor",
    FULL_TIME: "Full Time",
    INTERN: "Intern",
    OTHER: "Other",
    PART_TIME: "Part Time",
    PER_DIEM: "Per Diem",
    TEMPORARY: "Temporary",
    VOLUNTEER: "Volunteer",
  };

  return (
    <div className="mt-10">
      <h3 className="font-normal">
        Employment Type: {labels[effectiveEmploymentType] ?? effectiveEmploymentType}
      </h3>
    </div>
  );
}

function taxonomyNameFromPosting(primaryRoleTaxonomyId: number) {
  return taxonomyById(primaryRoleTaxonomyId)?.name ?? "Healthcare";
}

function getImage(hiringOrg: HiringOrg, imageType: Image["imageType"]) {
  return hiringOrg.images.find((image) => image.imageType === imageType) ?? null;
}
